import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Muralys · Configurateur
 *
 * - Surface facturee = arrondi au m² superieur, calcule en cm² entiers (pas d'erreur d'arrondi flottant).
 * - Le prix vient TOUJOURS de la variante Shopify (finition x surface facturee).
 * - Le cadrage est stocke en proportions du fichier source (0 a 1) : independant de l'ecran,
 *   reproductible sur l'original haute definition. Jamais de capture d'ecran.
 */

const CM2_PER_M2 = 10000;
const CROP_FORMAT_VERSION = 1;

/** @param {number} value @param {number} min @param {number} max */
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
/** @param {number} value @param {number} digits */
const round = (value, digits) => Number(value.toFixed(digits));

class MuralysConfigurator extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.cfg = JSON.parse(this.querySelector('[data-mc-config]').textContent);
    this.i18n = this.cfg.i18n;
    this.variants = this.cfg.variants;
    this.byKey = new Map(this.variants.map((v) => [`${v.f}|${v.s}`, v]));
    this.maxSurface = Math.max(...this.variants.map((v) => v.s));

    const $ = (selector) => this.querySelector(selector);
    this.el = {
      from: $('[data-mc-from]'),
      width: $('[data-mc-width]'),
      height: $('[data-mc-height]'),
      error: $('[data-mc-error]'),
      stage: $('[data-mc-stage]'),
      frame: $('[data-mc-frame]'),
      ghost: $('[data-mc-ghost]'),
      img: $('[data-mc-img]'),
      tagW: $('[data-mc-tag-w]'),
      tagH: $('[data-mc-tag-h]'),
      hint: $('[data-mc-hint]'),
      zoom: $('[data-mc-zoom]'),
      zoomIn: $('[data-mc-zoom-in]'),
      zoomOut: $('[data-mc-zoom-out]'),
      reset: $('[data-mc-reset]'),
      pose: $('[data-mc-pose]'),
      recap: $('[data-mc-recap]'),
      real: $('[data-mc-real]'),
      billed: $('[data-mc-billed]'),
      calc: $('[data-mc-calc]'),
      poseRow: $('[data-mc-pose-row]'),
      posePrice: $('[data-mc-pose-price]'),
      total: $('[data-mc-total]'),
      ctaError: $('[data-mc-cta-error]'),
      cta: $('[data-mc-cta]'),
      file: $('[data-mc-file]'),
      drop: $('[data-mc-drop]'),
      fileLabel: $('[data-mc-file-label]'),
      fileError: $('[data-mc-file-error]'),
      fileInfo: $('[data-mc-file-info]'),
    };

    this.state = {
      width: null,
      height: null,
      finition: this.querySelector('[data-mc-fin]:checked')?.value ?? this.variants[0]?.f,
      cx: 0.5,
      cy: 0.5,
      zoom: 1,
      pose: false,
      showErrors: false,
      busy: false,
      file: null,
    };
    this.upload = Boolean(this.cfg.upload);

    this.#restoreFromUrl();

    // Dimensions de l'image source (en pixels). Les dimensions de l'original viennent de Shopify.
    this.src = { w: this.cfg.source.w, h: this.cfg.source.h };
    this.frameSize = { w: 0, h: 0 };
    this.pointers = new Map();

    this.#renderFrom();
    this.#loadImage();
    this.#bind();
    this.#initBar();

    new ResizeObserver(() => this.#render()).observe(this.el.stage);
    this.#render();
  }

  /* ------------------------------------------------------------------ */
  /* Modifier une ligne du panier                                        */
  /* ------------------------------------------------------------------ */

  /** Prerempli le configurateur depuis les parametres mc_* du lien « Modifier le cadrage ». */
  #restoreFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const width = parseInt(params.get('mc_w') ?? '', 10);
    const height = parseInt(params.get('mc_h') ?? '', 10);
    if (!width || !height) return;

    this.state.width = width;
    this.state.height = height;
    this.el.width.value = String(width);
    this.el.height.value = String(height);

    const radio = [...this.querySelectorAll('[data-mc-fin]')].find((input) => input.value === params.get('mc_fin'));
    if (radio) {
      radio.checked = true;
      this.state.finition = radio.value;
    }

    const [x, y, cw, ch, zoom] = ['mc_x', 'mc_y', 'mc_cw', 'mc_ch', 'mc_zoom'].map((key) => parseFloat(params.get(key) ?? ''));
    if ([x, y, cw, ch].every(Number.isFinite)) {
      this.state.cx = x + cw / 2;
      this.state.cy = y + ch / 2;
    }
    if (Number.isFinite(zoom)) this.state.zoom = clamp(zoom, 1, this.cfg.maxZoom);

    if (params.get('mc_pose') === '1' && this.el.pose) {
      this.el.pose.checked = true;
      this.state.pose = true;
    }
    this._replaceKey = params.get('mc_replace');
  }

  /** Apres l'ajout de la nouvelle configuration, retire l'ancienne ligne du panier. */
  #removeReplacedLine(key) {
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      const button = document.querySelector(`[data-key="${CSS.escape(key)}"] .cart-items__remove`);
      if (button) {
        button.click();
        clearInterval(timer);
      } else if (attempts > 25) {
        clearInterval(timer);
      }
    }, 200);
  }

  /* ------------------------------------------------------------------ */
  /* Image source                                                        */
  /* ------------------------------------------------------------------ */

  #loadImage() {
    const { url } = this.cfg.source;
    if (this.upload || !url) return;
    const probe = new Image();
    probe.decoding = 'async';
    probe.onload = () => {
      // Si Shopify ne fournit pas les dimensions de l'original, on prend celles du fichier charge (meme ratio).
      if (!this.src.w || !this.src.h) this.src = { w: probe.naturalWidth, h: probe.naturalHeight };
      this.el.img.src = url;
      this.el.ghost.src = url;
      this.#render();
    };
    probe.src = url;
  }

  /* ------------------------------------------------------------------ */
  /* Evenements                                                          */
  /* ------------------------------------------------------------------ */

  #bind() {
    const { width, height, frame, zoom, zoomIn, zoomOut, reset, pose, cta } = this.el;

    for (const input of [width, height]) {
      input.addEventListener('input', () => {
        const digits = input.value.replace(/\D/g, '').slice(0, 4);
        if (digits !== input.value) input.value = digits;
        this.state[input === width ? 'width' : 'height'] = digits ? parseInt(digits, 10) : null;
        this.#render();
      });
      input.addEventListener('change', () => {
        this.state.showErrors = true;
        this.#render();
      });
    }

    this.addEventListener('change', (event) => {
      const target = /** @type {HTMLInputElement} */ (event.target);
      if (target.matches('[data-mc-fin]')) {
        this.state.finition = target.value;
        this.#render();
      }
    });

    pose?.addEventListener('change', () => {
      this.state.pose = pose.checked;
      this.#render();
    });

    zoom.addEventListener('input', () => this.#setZoom(parseFloat(zoom.value)));
    zoomIn.addEventListener('click', () => this.#setZoom(this.state.zoom + 0.25));
    zoomOut.addEventListener('click', () => this.#setZoom(this.state.zoom - 0.25));
    reset.addEventListener('click', () => {
      this.state.cx = 0.5;
      this.state.cy = 0.5;
      this.state.zoom = 1;
      this.#render();
    });

    // Cadrage : glisser (souris, doigt, stylet) et pincer pour zoomer
    frame.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      frame.setPointerCapture(event.pointerId);
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        this.pinch = { distance: Math.hypot(a.x - b.x, a.y - b.y), zoom: this.state.zoom };
      }
    });

    frame.addEventListener('pointermove', (event) => {
      const previous = this.pointers.get(event.pointerId);
      if (!previous) return;
      const current = { x: event.clientX, y: event.clientY };
      this.pointers.set(event.pointerId, current);

      if (this.pointers.size === 1) {
        this.#pan(current.x - previous.x, current.y - previous.y);
      } else if (this.pointers.size === 2 && this.pinch) {
        const [a, b] = [...this.pointers.values()];
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        if (this.pinch.distance > 0) this.#setZoom(this.pinch.zoom * (distance / this.pinch.distance));
      }
    });

    const release = (event) => {
      this.pointers.delete(event.pointerId);
      if (this.pointers.size < 2) this.pinch = null;
    };
    frame.addEventListener('pointerup', release);
    frame.addEventListener('pointercancel', release);

    // Ctrl/Cmd + molette pour zoomer (la molette seule fait defiler la page)
    frame.addEventListener(
      'wheel',
      (event) => {
        if (!event.ctrlKey && !event.metaKey) return;
        event.preventDefault();
        this.#setZoom(this.state.zoom * Math.exp(-event.deltaY * 0.0025));
      },
      { passive: false }
    );

    // Clavier : fleches pour deplacer, + et - pour zoomer, 0 pour recentrer
    frame.addEventListener('keydown', (event) => {
      const step = 0.04 / this.state.zoom;
      const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
      if (moves[event.key]) {
        event.preventDefault();
        this.state.cx += moves[event.key][0];
        this.state.cy += moves[event.key][1];
        this.#render();
      } else if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        this.#setZoom(this.state.zoom + 0.25);
      } else if (event.key === '-' || event.key === '_') {
        event.preventDefault();
        this.#setZoom(this.state.zoom - 0.25);
      } else if (event.key === '0' || event.key === 'Home') {
        event.preventDefault();
        this.el.reset.click();
      }
    });

    cta.addEventListener('click', () => this.#onCta());

    if (this.upload && this.el.file) {
      this.el.file.addEventListener('change', () => this.#onFile(this.el.file.files?.[0] ?? null));
      const drop = this.el.drop;
      for (const type of ['dragenter', 'dragover']) {
        drop.addEventListener(type, (event) => {
          event.preventDefault();
          drop.classList.add('is-over');
        });
      }
      for (const type of ['dragleave', 'drop']) {
        drop.addEventListener(type, () => drop.classList.remove('is-over'));
      }
      drop.addEventListener('drop', (event) => {
        event.preventDefault();
        const file = event.dataTransfer?.files?.[0];
        if (!file) return;
        try {
          const transfer = new DataTransfer();
          transfer.items.add(file);
          this.el.file.files = transfer.files;
        } catch (error) {
          /* navigateur sans DataTransfer : le fichier est pris directement */
        }
        this.#onFile(file);
      });
    }
  }

  /** Image deposee par le client : controle, apercu et dimensions de l'original. */
  #onFile(file) {
    const { fileError, fileLabel, drop } = this.el;
    fileError.hidden = true;
    if (this._objectUrl) URL.revokeObjectURL(this._objectUrl);
    this._objectUrl = null;
    this.state.file = null;
    drop.classList.remove('has-file');

    if (!file) {
      this.#render();
      return;
    }
    const okType = /^image\/(jpeg|png|webp)$/i.test(file.type);
    if (!okType || file.size > 20 * 1024 * 1024) {
      fileError.textContent = okType ? this.i18n.fileErrSize : this.i18n.fileErrType;
      fileError.hidden = false;
      this.el.file.value = '';
      this.#render();
      return;
    }

    const url = URL.createObjectURL(file);
    const probe = new Image();
    probe.onload = () => {
      this._objectUrl = url;
      this.state.file = file;
      this.src = { w: probe.naturalWidth, h: probe.naturalHeight };
      this.state.cx = 0.5;
      this.state.cy = 0.5;
      this.state.zoom = 1;
      this.el.img.src = url;
      this.el.ghost.src = url;
      fileLabel.textContent = file.name;
      drop.classList.add('has-file');
      this.#render();
    };
    probe.onerror = () => {
      URL.revokeObjectURL(url);
      fileError.textContent = this.i18n.fileErrType;
      fileError.hidden = false;
      this.el.file.value = '';
      this.#render();
    };
    probe.src = url;
  }

  /** Libelle du bouton tant qu'il manque l'image (page « Votre image ») ou les dimensions. */
  #emptyLabel() {
    return this.upload && !this.state.file ? this.i18n.ctaNoFile : this.i18n.ctaEmpty;
  }

  #readyLabel() {
    return this.i18n.ctaReady;
  }

  /** Precision de l'image : nombre de pixels par centimetre une fois les dimensions saisies. */
  #renderFileInfo(compute) {
    const info = this.el.fileInfo;
    if (!info) return;
    if (!this.state.file || !compute.valid || !this.src.w) {
      info.hidden = true;
      return;
    }
    const pxPerCm = (this.src.w * (this.#geometry().w || 1)) / this.state.width;
    const low = pxPerCm < 12;
    info.textContent = low ? this.i18n.fileLow : this.i18n.fileOk;
    info.classList.toggle('is-warn', low);
    info.hidden = false;
  }

  /**
   * Barre d'achat fixe (mobile) : visible quand le bouton principal n'est plus a l'ecran.
   * Elle reprend le prix et la meme action que le bouton principal.
   */
  #initBar() {
    const name = (this.cfg.productTitle || '').replace(/^Papier Peint\s+/i, '');
    const bar = document.createElement('div');
    bar.className = 'mc__bar';
    bar.hidden = true;
    bar.innerHTML = `
      <div class="mc__bar-info"><span class="mc__bar-name"></span><span class="mc__bar-price"></span></div>
      <button type="button" class="mc__bar-cta"></button>`;
    bar.querySelector('.mc__bar-name').textContent = name;
    this.append(bar);
    this.el.bar = bar;
    this.el.barPrice = bar.querySelector('.mc__bar-price');
    this.el.barCta = bar.querySelector('.mc__bar-cta');

    this.el.barCta.addEventListener('click', () => {
      if (this.compute?.valid) {
        this.#onCta();
      } else {
        this.el.width.scrollIntoView({ block: 'center', behavior: 'smooth' });
        this.el.width.focus({ preventScroll: true });
      }
    });

    this._ctaVisible = true;
    new IntersectionObserver(([entry]) => {
      this._ctaVisible = entry.isIntersecting;
      this.#renderBar();
    }).observe(this.el.cta);
  }

  /** Paiement en 4 fois : total de la commande / 4, ou prix au m² / 4 tant que la surface n'est pas saisie. */
  #renderInstallments(compute) {
    const price = document.querySelector('[data-mc-installment]');
    if (!price) return;
    const unit = Math.min(...this.variants.filter((v) => v.s === 1).map((v) => v.p));
    const cents = compute?.valid ? compute.total : unit;
    if (!Number.isFinite(cents)) return;
    price.textContent = this.#money(Math.round(cents / 4));
    const per = document.querySelector('[data-mc-installment-unit]');
    if (per) per.hidden = !!compute?.valid;
  }

  #renderBar() {
    this.el.bar.hidden = this._ctaVisible;
    const compute = this.compute;
    this.#renderInstallments(compute);
    if (compute?.valid) {
      this.el.barPrice.textContent = this.#money(compute.total);
      this.el.barCta.textContent = !this.upload || this.state.file ? this.i18n.ctaReady : this.#emptyLabel();
    } else {
      const unit = Math.min(...this.variants.filter((v) => v.s === 1).map((v) => v.p));
      this.el.barPrice.textContent = Number.isFinite(unit) ? `${this.i18n.from} ${this.#money(unit)} / m²` : '';
      this.el.barCta.textContent = this.#emptyLabel();
    }
  }

  /** @param {number} zoom */
  #setZoom(zoom) {
    this.state.zoom = clamp(zoom, 1, this.cfg.maxZoom);
    this.#render();
  }

  /** @param {number} dx @param {number} dy  deplacement en pixels ecran */
  #pan(dx, dy) {
    const { k } = this.#geometry();
    if (!k || !this.src.w) return;
    this.state.cx -= dx / (k * this.src.w);
    this.state.cy -= dy / (k * this.src.h);
    this.#render();
  }

  /* ------------------------------------------------------------------ */
  /* Calcul                                                              */
  /* ------------------------------------------------------------------ */

  /** Dimensions, surfaces, variante et erreurs. Aucun prix n'est calcule ici : il vient de la variante. */
  #compute() {
    const { width, height, finition, pose } = this.state;
    const { min, max } = this.cfg;
    const result = { valid: false, error: '', real: 0, billed: 0, variant: null, total: 0 };

    const complete = Number.isInteger(width) && Number.isInteger(height);
    if (!complete) {
      if (this.state.showErrors && (width !== null || height !== null)) result.error = this.#message('errDims');
      return result;
    }
    const inRange = width >= min && width <= max && height >= min && height <= max;
    if (!inRange) {
      if (this.state.showErrors || (String(width).length >= 3 && String(height).length >= 3)) {
        result.error = this.#message('errDims');
      }
      return result;
    }

    const area = width * height; // cm² entiers
    result.real = area / CM2_PER_M2;
    result.billed = Math.ceil(area / CM2_PER_M2);

    if (result.billed > this.maxSurface) {
      result.error = this.#message('errMax');
      return result;
    }

    const variant = this.byKey.get(`${finition}|${result.billed}`);
    if (!variant) {
      result.error = this.i18n.errUnavailable;
      return result;
    }

    result.valid = true;
    result.variant = variant;
    result.total = variant.p + (pose && this.cfg.pose ? this.cfg.pose.price : 0);
    return result;
  }

  /** @param {'errDims' | 'errMax'} key */
  #message(key) {
    return this.i18n[key]
      .replace('{min}', this.cfg.min)
      .replace('{max}', key === 'errMax' ? this.maxSurface : this.cfg.max);
  }

  /* ------------------------------------------------------------------ */
  /* Cadrage : geometrie                                                 */
  /* ------------------------------------------------------------------ */

  /** Ratio du mur (largeur / hauteur), ou 4/3 tant que les dimensions ne sont pas saisies. */
  #ratio() {
    const { width, height } = this.state;
    const { min, max } = this.cfg;
    const ok = width >= min && width <= max && height >= min && height <= max;
    return ok ? width / height : 4 / 3;
  }

  #layoutFrame() {
    const ratio = this.#ratio();
    const available = this.el.stage.clientWidth - 28;
    const maxHeight = clamp(window.innerHeight * 0.55, 260, 420);
    const width = Math.max(120, Math.min(available, maxHeight * ratio));
    const height = width / ratio;
    this.frameSize = { w: width, h: height };
    this.el.frame.style.width = `${width}px`;
    this.el.frame.style.height = `${height}px`;
  }

  /**
   * Boite de cadrage en pixels du fichier source.
   * zoom = 1 : la plus grande zone possible au ratio du mur ; zoom > 1 : zone plus petite.
   */
  #geometry() {
    const { w: imgW, h: imgH } = this.src;
    const { w: frameW, h: frameH } = this.frameSize;
    if (!imgW || !imgH || !frameW || !frameH) return { k: 0 };

    const ratio = frameW / frameH;
    const cover = imgW / imgH > ratio ? { w: imgH * ratio, h: imgH } : { w: imgW, h: imgW / ratio };
    const cw = cover.w / this.state.zoom;
    const ch = cover.h / this.state.zoom;

    this.state.cx = clamp(this.state.cx, cw / 2 / imgW, 1 - cw / 2 / imgW);
    this.state.cy = clamp(this.state.cy, ch / 2 / imgH, 1 - ch / 2 / imgH);

    const k = frameW / cw; // pixels ecran par pixel source
    return {
      k,
      cw,
      ch,
      left: frameW / 2 - this.state.cx * imgW * k,
      top: frameH / 2 - this.state.cy * imgH * k,
      x: this.state.cx - cw / 2 / imgW,
      y: this.state.cy - ch / 2 / imgH,
      w: cw / imgW,
      h: ch / imgH,
    };
  }

  /* ------------------------------------------------------------------ */
  /* Rendu                                                               */
  /* ------------------------------------------------------------------ */

  #money(cents) {
    const currency = window.Shopify?.currency?.active || 'EUR';
    const whole = cents % 100 === 0;
    return new Intl.NumberFormat(document.documentElement.lang || 'fr', {
      style: 'currency',
      currency,
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }

  #area(value) {
    return `${new Intl.NumberFormat('fr', { maximumFractionDigits: 2 }).format(value)} m²`;
  }

  #renderFrom() {
    const unit = Math.min(...this.variants.filter((v) => v.s === 1).map((v) => v.p));
    if (Number.isFinite(unit)) {
      this.el.from.innerHTML = `<span class="mpromo-price">${this.i18n.from} <s class="mpromo-was">${this.#money(Math.round(unit / 0.8 / 100) * 100)}</s> <strong>${this.#money(unit)}</strong> / m²</span><span class="mpromo-badge mpromo-badge--side"><b>-20 %</b><small>jusqu'au 14 octobre</small></span>`;
    }
  }

  #render() {
    const compute = this.#compute();
    this.compute = compute;
    const { width, height } = this.state;

    // Champs et erreurs
    this.el.error.hidden = !compute.error;
    this.el.error.textContent = compute.error;
    if (compute.error === this.#message('errMax')) {
      const link = document.createElement('a');
      link.href = this.cfg.contactUrl;
      link.textContent = this.i18n.contact;
      this.el.error.append(' ', link);
    }
    for (const input of [this.el.width, this.el.height]) {
      if (compute.error) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
    }

    // Cadre de cadrage
    this.#layoutFrame();
    const g = this.#geometry();
    if (g.k && this.el.img.src) {
      const imgWidth = `${this.src.w * g.k}px`;
      const transform = `translate(${g.left}px, ${g.top}px)`;
      for (const image of [this.el.img, this.el.ghost]) {
        image.style.width = imgWidth;
        image.style.transform = transform;
      }
    }
    const hasDims = compute.real > 0;
    this.el.hint.hidden = hasDims;
    this.el.tagW.hidden = !hasDims;
    this.el.tagH.hidden = !hasDims;
    if (hasDims) {
      this.el.tagW.textContent = `${width} cm`;
      this.el.tagH.textContent = `${height} cm`;
    }
    this.el.zoom.value = String(this.state.zoom);
    this.el.zoom.setAttribute('aria-valuetext', `${this.i18n.zoomLabel} ${Math.round(this.state.zoom * 100)} %`);

    // Recapitulatif
    this.el.recap.hidden = !compute.valid;
    if (compute.valid) {
      const unit = compute.variant.p / compute.billed;
      this.el.real.textContent = this.#area(compute.real);
      this.el.billed.textContent = `${compute.billed} m²`;
      this.el.calc.textContent = `${compute.billed} m² × ${this.#money(unit)} = ${this.#money(compute.variant.p)}`;
      const withPose = this.state.pose && this.cfg.pose;
      this.el.poseRow.hidden = !withPose;
      if (withPose) this.el.posePrice.textContent = this.#money(this.cfg.pose.price);
      this.el.total.innerHTML = `<s class="mpromo-was">${this.#money(Math.round(compute.total / 0.8 / 100) * 100)}</s> ${this.#money(compute.total)}`;
    }

    this.#renderBar();
    this.#renderFileInfo(compute);

    // Bouton
    if (!this.state.busy && !this._justAdded) {
      this.el.cta.textContent =
        compute.valid && (!this.upload || this.state.file)
          ? `${this.#readyLabel()} · ${this.#money(compute.total)}`
          : this.#emptyLabel();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Ajout au panier                                                     */
  /* ------------------------------------------------------------------ */

  #onCta() {
    if (this.state.busy) return;
    this.state.showErrors = true;
    this.#render();

    const compute = this.compute;
    if (this.upload && !this.state.file) {
      document.getElementById(this.el.file.id)?.closest('fieldset')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      this.el.fileError.textContent = this.i18n.ctaNoFile;
      this.el.fileError.hidden = false;
      return;
    }
    if (!compute.valid) {
      // Invitation : on amene le client sur le premier champ a completer
      const { min, max } = this.cfg;
      const widthOk = Number.isInteger(this.state.width) && this.state.width >= min && this.state.width <= max;
      (widthOk ? this.el.height : this.el.width).focus();
      return;
    }
    this.#addToCart(compute);
  }

  /**
   * Page « Votre image » : le fichier du client ne peut pas passer par l'API JSON du panier.
   * On l'envoie avec un vrai formulaire (multipart) vers /cart/add ; Shopify joint le fichier a la ligne de commande.
   */
  async #addUpload(compute, items) {
    this.state.busy = true;
    this.el.cta.setAttribute('aria-busy', 'true');
    this.el.cta.textContent = this.i18n.adding;
    this.el.ctaError.hidden = true;

    try {
      // La pose (si choisie) s'ajoute d'abord, par l'API habituelle
      const pose = items.find((item) => item.id === this.cfg.pose?.id);
      if (pose) {
        const response = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [pose] }),
        }).then((r) => r.json());
        if (response.status) throw new Error(response.description || response.message);
      }

      const item = items.find((entry) => entry !== pose);
      const form = document.createElement('form');
      form.method = 'post';
      form.action = '/cart/add';
      form.enctype = 'multipart/form-data';
      form.hidden = true;
      const add = (name, value) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.append(input);
      };
      add('id', String(item.id));
      add('quantity', '1');
      add('return_to', '/cart');
      for (const [key, value] of Object.entries(item.properties)) add(`properties[${key}]`, value);
      add('properties[_upload]', '1');

      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.name = 'properties[Votre image]';
      const transfer = new DataTransfer();
      transfer.items.add(this.state.file);
      fileInput.files = transfer.files;
      form.append(fileInput);

      document.body.append(form);
      form.submit();
    } catch (error) {
      console.error(error);
      this.#showCtaError(error?.message || this.i18n.errGeneric);
      this.state.busy = false;
      this.el.cta.removeAttribute('aria-busy');
      this.#render();
    }
  }

  /** @param {{ valid: boolean, real: number, billed: number, variant: any, total: number }} compute */
  async #addToCart(compute) {
    const { width, height, finition, pose } = this.state;
    const g = this.#geometry();
    const ref = (window.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)).slice(0, 8);
    const unit = compute.variant.p / compute.billed;

    const crop = {
      v: CROP_FORMAT_VERSION,
      x: round(g.x, 5),
      y: round(g.y, 5),
      w: round(g.w, 5),
      h: round(g.h, 5),
      zoom: round(this.state.zoom, 3),
      src: { w: this.src.w, h: this.src.h, media: this.cfg.source.mediaId },
    };

    const items = [
      {
        id: compute.variant.id,
        quantity: 1,
        properties: {
          Dimensions: `${width} × ${height} cm`,
          'Surface réelle': this.#area(compute.real),
          'Surface facturée': `${compute.billed} m²`,
          'Prix au m²': this.#money(unit),
          _largeur_cm: String(width),
          _hauteur_cm: String(height),
          _cadrage: JSON.stringify(crop),
          _crop_x: String(crop.x),
          _crop_y: String(crop.y),
          _crop_w: String(crop.w),
          _crop_h: String(crop.h),
          _crop_zoom: String(crop.zoom),
          _ref: ref,
        },
      },
    ];
    if (pose && this.cfg.pose) {
      // Le panier affiche en haut la derniere ligne ajoutee : on ajoute la pose en premier pour que le decor reste au-dessus.
      items.unshift({ id: this.cfg.pose.id, quantity: 1, properties: { _ref: ref } });
    }

    if (this.upload) return this.#addUpload(compute, items);

    this.state.busy = true;
    this.el.cta.setAttribute('aria-busy', 'true');
    this.el.cta.textContent = this.i18n.adding;
    this.el.ctaError.hidden = true;

    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
      .filter(Boolean)
      .join(',');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections }),
      }).then((r) => r.json());

      const cart = await this.#refreshCart();
      const failed = Boolean(response.status);

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'product-form-component',
          sourceId: this.id,
          itemCount: items.length,
          productId: this.dataset.productId,
          sections: response.sections,
          didError: failed,
        },
      });

      if (failed) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        this.#showCtaError(response.description || response.message);
      } else {
        if (this._replaceKey) {
          this.#removeReplacedLine(this._replaceKey);
          this._replaceKey = null;
        }
        this._justAdded = true;
        this.el.cta.textContent = this.i18n.added;
        setTimeout(() => {
          this._justAdded = false;
          this.#render();
        }, 2500);
      }
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Network error', code: 'SERVICE_UNAVAILABLE' }));
      this.#showCtaError(this.i18n.errGeneric);
    } finally {
      this.state.busy = false;
      this.el.cta.removeAttribute('aria-busy');
      this.#render();
    }
  }

  #showCtaError(message) {
    this.el.ctaError.textContent = message || this.i18n.errGeneric;
    this.el.ctaError.hidden = false;
  }

  async #refreshCart() {
    const cartItems = /** @type {any} */ (document.querySelector('cart-items-component'));
    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      return cartItems.fetchCartData();
    }
    return fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.json`).then((r) => r.json());
  }
}

if (!customElements.get('muralys-configurator')) {
  customElements.define('muralys-configurator', MuralysConfigurator);
}
